import { useState } from 'react';
import useDataService from '../../hooks/useDataService';
import { eMessageType } from '../../types/IMessageType';
import { fetchData } from '../../_lib/lib';
import { checkForSuccesInvocation } from '../../_lib/utils';
import { useConfirm } from '../../context/ConfirmContext';

export interface IProps {
  syncFor: string;
}

const useVerticalSync = ({ syncFor }: IProps) => {
  const { confirm } = useConfirm();
  const [environment, setEnvironment] = useState('');
  const [release, setRelease] = useState('');
  const [processState, setProcessState] = useState<number>(0);
  const [previewState, setPreviewState] = useState<any>(null);
  const [reviewQueriesConfirmationDialog, setReviewQueriesConfirmationDialog] = useState<boolean>(false);
  const [reviewData, setReviewData] = useState<{
    queryData: any[] | null;
    currentTotalApprovers?: number;
    currentlyApprovedBy?: string[];
    newQueriesCount?: number;
  }>({
    queryData: null,
    currentTotalApprovers: undefined,
    currentlyApprovedBy: undefined,
    newQueriesCount: undefined,
  });
  const { openSnackBar } = useDataService();

  const schemaSync = async () => {
    setProcessState(1);
    const res: any = await fetchData(
      'PATCH',
      `schema-sync${syncFor !== 'master' ? '-slave' : ''}/vertical-sync-v2`,
      {
        body: { environment, release },
      }
    );
    if (!res.error) {
      try {
        checkForSuccesInvocation(res, setPreviewState, setProcessState);
      } catch (e: any) {
        openSnackBar(
          e.message || 'Something went wrong, Unable to check status',
          eMessageType.error
        );
      }
    } else {
      setProcessState(0);
      setPreviewState(null);
      openSnackBar('Vertical Sync Failed', eMessageType.error);
    }
  };

  const openReviewQueriesConfirmationDialog = async () => {
    setReviewQueriesConfirmationDialog(true);
    setReviewData({
      queryData: null,
      currentTotalApprovers: undefined,
      currentlyApprovedBy: undefined,
      newQueriesCount: undefined,
    });
    const res: any = await fetchData('PATCH', `schema-sync/schema-review-before-sync`, {
      body: { environment, release },
    });
    if (!res.error) {
      const data = Array.isArray(res.queries) ? res.queries : [];
      setReviewData({
        queryData: data,
        currentTotalApprovers: res.currentTotalApprovers,
        currentlyApprovedBy: res.currentlyApprovedBy,
        newQueriesCount: res.newQueriesCount,
      });
    } else {
      openSnackBar(res.message || 'Failed to fetch queries', eMessageType.error);
      closeReviewQueriesConfirmationDialog();
    }
  };

  const closeReviewQueriesConfirmationDialog = () => {
    setReviewQueriesConfirmationDialog(false);
    setReviewData({
      queryData: null,
      currentTotalApprovers: undefined,
      currentlyApprovedBy: undefined,
      newQueriesCount: undefined,
    });
  };

  const onApprove = async () => {
    const res: any = await fetchData('PATCH', `schema-sync/schema-sync-approval`, {
      body: { environment, release },
    });
    if (!res.error) {
      openSnackBar(res.message || 'Queries approved successfully', eMessageType.success);
    } else {
      openSnackBar(res.message || 'Approve queries failed', eMessageType.error);
    }
  };

  return {
    environment,
    setEnvironment,
    release,
    setRelease,
    processState,
    setProcessState,
    previewState,
    reviewQueriesConfirmationDialog,
    reviewData,
    schemaSync,
    openReviewQueriesConfirmationDialog,
    closeReviewQueriesConfirmationDialog,
    onApprove,
    confirm,
  };
};

export default useVerticalSync;
